import React from "react";
import CustomModal from "../../components/modal/CustomModal";
import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../axios/axiosInstance";
import UserCreateForm from "./components/UserCreateForm";
import FamilyDetailsTab from "./components/FamilyDetailsTab";
import CreateUserAccountTab from "./components/CreateUserAccountTab";
import PermissionGuard from "../../components/PermissionGuard";
import { hasPermissionSync } from "../../utils/get-user-permissions";

const getZoneAdminZoneIds = (): number[] => {
  try {
    const s = localStorage.getItem("zone_admin_zone_ids");
    if (!s) return [];
    const arr = JSON.parse(s);
    return Array.isArray(arr) ? arr.map(Number) : [];
  } catch {
    return [];
  }
};

const vehicleTypes = [
  { value: "car", label: "Car" },
  { value: "bike", label: "Bike" },
  { value: "truck", label: "Truck" },
  { value: "motorcycle", label: "Motorcycle" },
  { value: "bus", label: "Bus" },
  { value: "van", label: "Van" },
  { value: "other", label: "Other" },
];

interface UserDetailsProps {
  forceMemberId?: string | null;
  isOwnProfile?: boolean;
}

const UserDetails: React.FC<UserDetailsProps> = ({ forceMemberId, isOwnProfile = false }) => {
  const role: any = localStorage.getItem("role");
  const params = useParams();
  const id = forceMemberId != null ? { id: forceMemberId } : params;
  const navigate = useNavigate();
  const location = useLocation();
  const from = isOwnProfile ? "/" : (location.state?.from?.pathname || "/members");
  const zoneAdminZoneIds = getZoneAdminZoneIds();
  const isZoneAdmin = zoneAdminZoneIds.length > 0;
  const canEditThisMember = (): boolean => {
    if (isOwnProfile) return false;
    if (hasPermissionSync("member.update") || hasPermissionSync("member.manage") || hasPermissionSync("user.update") || hasPermissionSync("user.manage")) return true;
    if (isZoneAdmin && data?.zone?.id != null && zoneAdminZoneIds.includes(Number(data.zone.id))) return true;
    return false;
  };
  const [data, setData] = useState<any>();
  const [open, setOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<any>();
  const [initialFromData, setInitialFormData] = useState<any>();
  const [vehicle, setVehicle] = useState<any>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "family" | "account">("overview");

  const handleOpen = () => {
    setFormData(null);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleGet = async (id: any) => {
    try {
      const resp: any = await axiosInstance.get(`/api/user/${id}`);
      if (resp?.status === 200) {
        setData(resp.data?.data);
      }
    } catch (Error: any) {
      const errorMessage = Error?.response?.data?.message || Error?.message || "Error getting user";
      if (errorMessage.includes("Super Admin")) {
        toast.error(errorMessage);
        // Navigate back if trying to access Super Admin as non-Super Admin
        const isSuperAdmin = localStorage.getItem("isSuperAdmin") === "true" || Number(role) === 0;
        if (!isSuperAdmin) {
          setTimeout(() => navigate("/members"), 2000);
        }
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleGetVehicle = async (id: any) => {
    try {
      const resp: any = await axiosInstance.get(`/api/vehicle/${id}`);
      if (resp?.status === 200) {
        setOpen(true);
        setFormData(resp.data?.data);
        setInitialFormData(resp?.data?.data);
      }
    } catch (Error: any) {
      toast.error("Error getting vehicle");
    }
  };

  const handleGetByUserId = async (id: any) => {
    try {
      const resp: any = await axiosInstance.get(`/api/vehicle/${id}/user`);
      if (resp?.status === 200) {
        setVehicle(resp.data?.data);
      }
    } catch (Error: any) {
      toast.error("Error getting vehicle");
    }
  };

  const handleSave = async () => {
    if (!formData?.reg_no) {
      toast.error("Registration number is required");
      return;
    }
    if (!formData?.type) {
      toast.error("Vehicle type is required");
      return;
    }
    formData.role = role;
    formData.user_id = id?.id;
    if (!formData?.id) {
      try {
        const resp: any = await axiosInstance.post(
          `/api/vehicle?invocation=${role}`,
          formData
        );
        if (resp?.status === 200) {
          handleClose();
          if (role == 0) {
            toast.success("Vehicle created successfully");
          } else {
            toast.success("Vehicle create srequest sent to Super Admin");
          }
          handleGetByUserId(id.id);
        }
      } catch (Error: any) {
        toast.error(Error?.response?.data?.body);
      }
    } else {
      try {
        const noFieldChanges =
          JSON.stringify(formData) === JSON.stringify(initialFromData);
        if (noFieldChanges) {
          toast.info("No changes were made");
          return;
        }
        const resp: any = await axiosInstance.put(
          `/api/vehicle/${formData?.id}?invocation=${role}`,
          formData
        );
        if (resp?.status === 200) {
          handleClose();
          if (role == 0) {
            toast.success("Vehicle Details updated successfully");
          } else {
            toast.success("Vehicle details request sent to Super Admin");
          }
          handleGetByUserId(id.id);
        }
      } catch (Error: any) {
        toast.error(Error?.response?.data?.body);
      }
    }
  };

  const handleDelete = async (vid: any) => {
    try {
      const resp: any = await axiosInstance.delete(
        `/api/vehicle/${vid}?invocation=${role}`
      );
      if (resp?.status === 200) {
        if (!role) {
          toast.success("Vehicle deleted successfully");
        } else {
          toast.success("Vehicle deletion request send successfully");
        }
        handleGetByUserId(id.id);
      }
    } catch (Error: any) {
      toast.error("Error delete vehicle");
    }
  };

  const handleChange = (key: any, value: any) => {
    setFormData({ ...formData, [key]: value });
  };

  useEffect(() => {
    handleGet(id.id);
    handleGetByUserId(id.id);
    // eslint-disable-next-line
  }, [id.id]);

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header Section */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate(from)}
            className="btn-primary flex items-center gap-2 px-4 py-2"
          >
            <i className="fa-solid fa-arrow-left"></i>
            <span>{isOwnProfile ? "Back to Dashboard" : "Back to Members"}</span>
          </button>
          {!isOwnProfile && canEditThisMember() && (
            <button
              onClick={() => setEditOpen(true)}
              className="btn-primary flex items-center gap-2 px-4 py-2"
            >
              <i className="fa-solid fa-edit"></i>
              <span>Edit Member</span>
            </button>
          )}
        </div>

        {/* Profile Header Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
            <div className="relative">
              {data?.image_path ? (
                <img
                  src={`${process.env.REACT_APP_API_URL || 'http://localhost:3070'}/${data.image_path}`}
                  alt={data?.name || "Member"}
                  className="w-35 h-35 rounded-full object-cover shadow-xl border-4 border-white dark:border-gray-700"
                  style={{ width: '140px', height: '140px' }}
                />
              ) : (
                <div
                  className="w-35 h-35 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold shadow-xl border-4 border-white dark:border-gray-700"
                  style={{ width: '140px', height: '140px', fontSize: '3rem' }}
                >
                  {data?.name ? data.name.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'M'}
                </div>
              )}
              {(data?.date_of_death || data?.profile?.date_of_death) && (
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-red-600 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                  Deceased
                </div>
              )}
            </div>
            <div className="flex-1 w-full">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {data?.name || "Member Name"}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3">
                    {data?.is_leader && (
                      <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm font-semibold">
                        <i className="fa-solid fa-star mr-1"></i>Leader
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      data?.is_whatsapp_enabled 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}>
                      <i className={`fa-brands fa-whatsapp mr-1 ${data?.is_whatsapp_enabled ? '' : 'opacity-50'}`}></i>
                      WhatsApp {data?.is_whatsapp_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    {data?.member_type && (
                      <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-semibold capitalize">
                        {data.member_type}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex overflow-x-auto border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-6 py-4 font-semibold text-sm transition-colors whitespace-nowrap border-b-2 ${
                activeTab === "overview"
                  ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <i className="fa-solid fa-user mr-2"></i>
              Overview
            </button>
            <button
              onClick={() => setActiveTab("family")}
              className={`px-6 py-4 font-semibold text-sm transition-colors whitespace-nowrap border-b-2 ${
                activeTab === "family"
                  ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <i className="fa-solid fa-users mr-2"></i>
              Family Details
            </button>
            {!isOwnProfile && (Number(role) === 0 || hasPermissionSync("admin")) && (
              <button
                onClick={() => setActiveTab("account")}
                className={`px-6 py-4 font-semibold text-sm transition-colors whitespace-nowrap border-b-2 ${
                  activeTab === "account"
                    ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <i className="fa-solid fa-user-plus mr-2"></i>
                User Account
              </button>
            )}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <>
            {/* Information Cards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Personal Information Card */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <i className="fa-solid fa-user text-blue-600 dark:text-blue-400"></i>
              Personal Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-1">Full Name</div>
                <div className="text-base font-medium text-gray-900 dark:text-white">{data?.name || "-"}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-1">Gender</div>
                <div className="text-base font-medium text-gray-900 dark:text-white capitalize">
                  {data?.gender || data?.profile?.gender || "-"}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-1">Date of Birth</div>
                <div className="text-base font-medium text-gray-900 dark:text-white">
                  {formatDate(data?.date_of_birth || data?.profile?.date_of_birth)}
                </div>
              </div>
              {(data?.date_of_death || data?.profile?.date_of_death) && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-1">Date of Death</div>
                  <div className="text-base font-medium text-red-600 dark:text-red-400">
                    {formatDate(data.profile.date_of_death)}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-1">Phone Number</div>
                <div className="text-base font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <i className="fa-solid fa-phone text-gray-400"></i>
                  {data?.phone || "-"}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-1">Zone</div>
                <div className="text-base font-medium text-gray-900 dark:text-white">
                  {data?.zone?.zone_name ? (
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-sm">
                      {data.zone.zone_name}
                    </span>
                  ) : "-"}
                </div>
              </div>
              {data?.city && (
                <div className="md:col-span-2">
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-1">Location</div>
                  <div className="text-base font-medium text-gray-900 dark:text-white flex items-start gap-2 flex-wrap">
                    <i className="fa-solid fa-map-marker-alt text-gray-400 mt-1"></i>
                    <div className="flex flex-wrap items-center gap-2">
                      {data.city.name && (
                        <span className="px-2 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded text-sm font-semibold">
                          {data.city.name}
                        </span>
                      )}
                      {data.city.taluk?.name && (
                        <span className="px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded text-sm">
                          {data.city.taluk.name}
                        </span>
                      )}
                      {data.city.taluk?.district?.name && (
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-sm">
                          {data.city.taluk.district.name}
                        </span>
                      )}
                      {data.city.taluk?.district?.state?.name && (
                        <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-sm">
                          {data.city.taluk.district.state.name}
                        </span>
                      )}
                      {data.city.taluk?.district?.state?.country?.name && (
                        <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-sm">
                          {data.city.taluk.district.state.country.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="md:col-span-2">
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-1">Address</div>
                <div className="text-base font-medium text-gray-900 dark:text-white flex items-start gap-2">
                  <i className="fa-solid fa-location-dot text-gray-400 mt-1"></i>
                  <span>{data?.address || "-"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Religious Information Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <i className="fa-solid fa-cross text-blue-600 dark:text-blue-400"></i>
              Spiritual Information
            </h2>
            <div className="space-y-4">
              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-1">Date of Baptism</div>
                <div className="text-base font-medium text-gray-900 dark:text-white">
                  {formatDate(data?.baptism_date)}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-1">First Communion</div>
                <div className="text-base font-medium text-gray-900 dark:text-white">
                  {formatDate(data?.first_communion_date)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information & Vehicles */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* APARTMENT FUNCTIONALITY DISABLED */}
          {/* Companies/Apartments Card */}
          {/* {data?.appartments && data.appartments.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <i className="fa-solid fa-building text-blue-600 dark:text-blue-400"></i>
                Companies/Apartments
              </h2>
              <div className="space-y-3">
                {data.appartments.map((apt: any, idx: number) => (
                  <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="font-semibold text-gray-900 dark:text-white">{apt?.appartment_name || "N/A"}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">{apt?.apartment_type || ""}</div>
                  </div>
                ))}
              </div>
            </div>
          )} */}
          {/* Vehicles Section */}
          <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 lg:col-span-3`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <i className="fa-solid fa-car text-blue-600 dark:text-blue-400"></i>
                Vehicles
              </h2>
              {!isOwnProfile && (
                <PermissionGuard permission={["vehicle.create", "vehicle.manage"]}>
                  <button
                    type="button"
                    onClick={handleOpen}
                    className="btn-primary flex items-center gap-2 px-4 py-2"
                  >
                    <i className="fa-solid fa-plus"></i>
                    <span>Add Vehicle</span>
                  </button>
                </PermissionGuard>
              )}
            </div>
            {vehicle && vehicle.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {vehicle.map((item: any, index: any) => (
                  <div
                    key={index}
                    className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                          <i className="fa-solid fa-car text-blue-600 dark:text-blue-400 text-xl"></i>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Registration</div>
                          <div className="text-lg font-bold text-gray-900 dark:text-white">{item?.reg_no}</div>
                        </div>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Type</div>
                      <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-semibold capitalize">
                        {item?.type || "N/A"}
                      </span>
                    </div>
                    {!isOwnProfile && (
                    <div className="flex gap-2">
                      <PermissionGuard permission={["vehicle.update", "vehicle.manage"]}>
                        <button
                          onClick={() => handleGetVehicle(item?.id)}
                          className="btn-primary flex-1 px-3 py-2 text-sm flex items-center justify-center gap-2"
                        >
                          <i className="fa-solid fa-edit"></i>
                          <span>Edit</span>
                        </button>
                      </PermissionGuard>
                      <PermissionGuard permission={["vehicle.delete", "vehicle.manage"]}>
                        <button
                          onClick={() => handleDelete(item?.id)}
                          className="btn-primary px-3 py-2 text-sm"
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </PermissionGuard>
                    </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                <i className="fa-solid fa-car text-4xl text-gray-400 dark:text-gray-600 mb-3"></i>
                <p className="text-gray-500 dark:text-gray-400 font-medium">No vehicles registered</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Click "Add Vehicle" to register a vehicle</p>
              </div>
            )}
          </div>
        </div>
          </>
        )}

        {activeTab === "family" && (
          <FamilyDetailsTab
            memberId={Number(id.id)}
            memberData={data}
            onRefresh={() => handleGet(id.id)}
          />
        )}

        {activeTab === "account" && !isOwnProfile && (Number(role) === 0 || hasPermissionSync("admin")) && (
          <CreateUserAccountTab
            memberId={Number(id.id)}
            memberName={data?.name}
            memberPhone={data?.phone}
          />
        )}
      </div>
      {/* Vehicle Modal */}
      <CustomModal open={open} onClose={handleClose}>
        <div className="p-8 w-full max-w-md">
          {/* Close Button */}
          <button
            type="button"
            className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            onClick={handleClose}
            aria-label="Close"
          >
            <i className="fa-solid fa-times text-xl"></i>
          </button>
          {/* Vehicle Icon/Illustration */}
          <div className="flex justify-center mb-4">
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30">
              <i className="fa-solid fa-car text-blue-600 dark:text-blue-400 text-2xl"></i>
            </span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 text-center">{formData?.id ? "Edit Vehicle" : "Add Vehicle"}</h3>
          <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Registration No <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData?.reg_no || ""}
                onChange={(e: any) => handleChange("reg_no", e?.target.value)}
                className="dark-input w-full px-4 py-2.5"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Vehicle Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData?.type || ""}
                onChange={(e: any) => handleChange("type", e?.target.value)}
                className="dark-input w-full px-4 py-2.5"
              >
                <option value="">Select Vehicle Type</option>
                {vehicleTypes.map((item: any) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-8">
              <button
                type="button"
                onClick={handleClose}
                className="btn-primary px-4 py-2.5"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary px-6 py-2.5"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </CustomModal>
      {/* User Edit Modal */}
      <CustomModal open={editOpen} onClose={() => {
        setEditOpen(false);
        handleGet(id.id);
      }}>
        <UserCreateForm onClose={() => {
          setEditOpen(false);
          handleGet(id.id);
        }} id={id.id} requestData={{ ...data, img_path: data?.image_path }} />
      </CustomModal>
    </div>
  );
};

export default UserDetails;
